import { useLoad } from "@redotech/react-util/load";
import { BrandKit } from "@redotech/redo-model/brand-kit";
import { MarketingForgeElementType } from "@redotech/redo-model/forge/marketing-signup-forge-types";
import {
  SignupForm as ISignupForm,
  SignupFormSiteType,
} from "@redotech/redo-model/signup-form";
import { BaseTeaser } from "@redotech/redo-web/marketing/base-teaser";
import { SignupForm } from "@redotech/redo-web/marketing/signup-form";
import { MerchantCustomizableModal } from "@redotech/redo-web/storefront/merchant-customizable-modal";
import { memo, useCallback, useEffect, useState } from "react";
import { FixedContainer } from "./fixed-container";

const SIGNUP_LOCAL_STORAGE_KEY = "redo.marketing-signup-form";
const EXPIRATION_PERIOD = 24 * 60 * 60 * 1000;

interface SignupFormClient {
  getSignupForm: (
    params: {
      shopifyCustomerId: string | null;
      customerHash: string;
      formId: string | null;
      isOnMobile: boolean;
      site: SignupFormSiteType;
      utmParams: string;
    },
    { signal }: { signal: AbortSignal },
  ) => Promise<{
    form?: ISignupForm;
    formId?: string | null;
    skipPhoneSteps?: boolean;
    reason?: string;
  }>;

  logSignupFormAnalytics: (params: {
    idempotencyKey: string;
    formId: string;
    type: "view" | "submit";
  }) => Promise<{ success?: boolean }>;

  submitSignupFormStep: (params: {
    formId: string;
    stepIndex: number;
    formData: {
      email?: string;
      phone?: string;
    };
    customerId: string | null;
    entryPoint: string;
  }) => Promise<{
    success?: boolean;
    error?: string;
    customerId?: string | null;
  }>;
}

export const SignupFormInstance = memo(function SignupFormInstance(props: {
  brandKit: BrandKit;
  rpcClient?: SignupFormClient;
  utmParams?: string;
  shopifyCustomerId: string | null;
  site: SignupFormSiteType;
  entryPoint: string;
  onShouldDisplayChanged?: (shouldDisplay: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const [hasEverBeenOpened, setHasEverBeenOpened] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [localData, setLocalData] = useState<SignupFormLocalData>(() => {
    const stored = localStorage.getItem(SIGNUP_LOCAL_STORAGE_KEY);
    return stored ? parseLocalData(stored) : {};
  });
  const client = props.rpcClient;

  useEffect(() => {
    localStorage.setItem(SIGNUP_LOCAL_STORAGE_KEY, JSON.stringify(localData));
  }, [localData]);

  const shopifyCustomerId = props.shopifyCustomerId;

  const [customerId, setCustomerId] = useState<string | null>(null);

  const formLoad = useLoad(
    async (signal) => {
      if (!client) return undefined;
      if (!localData.customerHash)
        return console.error("missing customer hash"), undefined;
      const result = await client.getSignupForm(
        {
          customerHash: localData.customerHash?.toString() ?? null,
          shopifyCustomerId: shopifyCustomerId,
          formId: localData.formId?.toString() ?? null,
          isOnMobile: isOnMobile(),
          site: props.site,
          utmParams: props.utmParams ?? "",
        },
        { signal },
      );
      if (result.formId) {
        setLocalData((prevData) => ({
          ...prevData,
          formId: result.formId ?? undefined,
        }));
      }
      return result;
    },
    [client, localData.customerHash, shopifyCustomerId, props.utmParams],
  );

  const form = formLoad.value?.form as ISignupForm | undefined;
  const formId = formLoad.value?.formId;

  useEffect(() => {
    if (!localData.customerHash) {
      const customerHash = shopifyCustomerId ?? generateAnonymousCustomerId();
      setLocalData((prevData) => ({
        ...prevData,
        customerHash,
      }));
    }
  }, [localData.customerHash, shopifyCustomerId]);

  useEffect(() => {
    if (completed || !form || !formId) return;
    if (!form.behavior.display.timeOnPage.enabled) return;

    const previousAutoOpenExpired =
      localData.formAutoOpenedAt &&
      new Date().getTime() - localData.formAutoOpenedAt.getTime() >
        EXPIRATION_PERIOD;

    if (localData.formAutoOpenedAt && !previousAutoOpenExpired) return;

    const timeout = setTimeout(() => {
      if (localData.formAutoOpenedAt) return;
      setLocalData((prevData) => ({
        ...prevData,
        formAutoOpenedAt: new Date(),
      }));
      openModal().catch((e) =>
        console.error("Failed to auto-open signup form", e),
      );
    }, form.behavior.display.timeOnPage.seconds * 1000);
    return () => clearTimeout(timeout);
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, localData, formId, completed]);

  const getNextStepIndex = useCallback(
    (startIndex: number): number => {
      if (!form) return startIndex;
      const skipPhoneSteps = formLoad.value?.skipPhoneSteps ?? true;
      let stepIndex = startIndex;

      while (
        skipPhoneSteps &&
        stepIndex < form?.steps.length &&
        form.steps[stepIndex]?.elements.some(
          (el) =>
            el.type === MarketingForgeElementType.INPUT &&
            el.inputType === "phone",
        )
      ) {
        stepIndex++;
      }

      return stepIndex;
    },
    [form, formLoad.value?.skipPhoneSteps],
  );

  const submitStep = useCallback(
    async (i: number, formData: Record<string, string>) => {
      if (!form || !formId || !client) return;
      const result = await client.submitSignupFormStep({
        formId,
        stepIndex: i,
        formData,
        customerId,
        entryPoint: props.entryPoint,
      });

      if (!result.success) {
        console.error("Failed to submit form step", result);
        return;
      }

      if (result.customerId) {
        setCustomerId(result.customerId);
      }

      if (!localData.atLeastOneStepSubmittedAt && localData.customerHash) {
        setLocalData((prevData) => ({
          ...prevData,
          atLeastOneStepSubmittedAt: new Date(),
        }));
        await client.logSignupFormAnalytics({
          idempotencyKey: localData.customerHash,
          formId,
          type: "submit",
        });
      }

      const nextStepIndex = getNextStepIndex(i + 1);
      const hasNextStep = nextStepIndex < form.steps.length;

      if (hasNextStep) {
        setCurrentStep(nextStepIndex);
      } else {
        setCurrentStep(0);
        setOpen(false);
        setCompleted(true);
      }
    },
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form, formId, client, customerId, localData, getNextStepIndex],
  );

  const openModal = useCallback(async () => {
    if (open) return;
    setOpen(true);
    setHasEverBeenOpened(true);

    const initialStep = getNextStepIndex(0); // TODO: resume from last step?
    setCurrentStep(initialStep);

    if (!localData.formViewedAt && localData.customerHash && client && formId) {
      setLocalData((prevData) => ({
        ...prevData,
        formViewedAt: new Date(),
      }));
      await client.logSignupFormAnalytics({
        idempotencyKey: localData.customerHash,
        formId,
        type: "view",
      });
    }
  }, [open, getNextStepIndex, localData, client, formId]);

  const teaser = form?.teaser;
  const isCorrectSite = props.site === form?.behavior.appearsOnSite;
  const shouldDisplay = !!(form && isCorrectSite);
  const showTeaser =
    isCorrectSite &&
    (teaser?.visibility === "after-closing"
      ? hasEverBeenOpened && !completed
      : !!teaser);

  const opacityHex = form
    ? Math.floor((form.theme.overlay.opacityPercent / 100) * 255)
        .toString(16)
        .padStart(2, "0")
    : 0;

  useEffect(() => {
    props.onShouldDisplayChanged?.(shouldDisplay);
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldDisplay, props.onShouldDisplayChanged]);

  return (
    <>
      {teaser && showTeaser && !localData.atLeastOneStepSubmittedAt && (
        <FixedContainer teaser={teaser}>
          <BaseTeaser onClick={openModal} teaser={teaser} />
        </FixedContainer>
      )}
      {form && (
        <MerchantCustomizableModal
          backdropColor={form.theme.overlay.color + opacityHex}
          fullscreen={form.layout.type === "fullscreen"}
          onClose={() => setOpen(false)}
          open={open && !completed}
        >
          <SignupForm
            brandKit={props.brandKit}
            currentStep={currentStep}
            form={form}
            onClose={() => setOpen(false)}
            onSubmitStep={submitStep}
          />
        </MerchantCustomizableModal>
      )}
    </>
  );
});

function parseLocalData(data: string): SignupFormLocalData {
  try {
    const raw = JSON.parse(data);
    return {
      customerHash: raw.customerHash,
      formAutoOpenedAt: raw.formAutoOpenedAt
        ? new Date(raw.formAutoOpenedAt)
        : undefined,
      formViewedAt: raw.formViewedAt ? new Date(raw.formViewedAt) : undefined,
      atLeastOneStepSubmittedAt: raw.atLeastOneStepSubmittedAt
        ? new Date(raw.atLeastOneStepSubmittedAt)
        : undefined,
      formId: raw.formId,
    };
  } catch (e) {
    console.error("Failed to parse signup form localStorage", e);
    return {};
  }
}

function isOnMobile(): boolean {
  if ((window.navigator as any).userAgentData?.mobile) return true;
  if (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    )
  )
    return true;
  return window.innerWidth < 768;
}

function generateAnonymousCustomerId() {
  return `anonymous-${Math.random().toString(36).substring(2)}`;
}

interface SignupFormLocalData {
  customerHash?: string;
  formViewedAt?: Date;
  formId?: string;
  formAutoOpenedAt?: Date;
  atLeastOneStepSubmittedAt?: Date;
}
